import { useEffect, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { lesionSchema } from "../../yup";
import Input from "../../components/UserInput/Input";
import { useUpdateLesionDetailsMutation } from "../../services/api";
import style from "./lesion.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { getErrorMessage } from "../../utils/common";

type FormState = typeof lesionSchema.__outputType;

type EditLesionNavState = {
  lesion?: Lesion;
};

/**
 * Edit Lesion page for updating an existing lesion.
 *
 * Notes:
 * - The row being edited is handed over in router state by the Lesions list, so
 *   this screen does not re-fetch it. Browsers persist history.state, so a
 *   reload still works — but a new tab or a shared link carries no state, which
 *   is why the no-state fallback below sends the user back to the list.
 * - Validation is enforced via `lesionSchema`.
 */
const EditLesion = () => {
  const [updateLesion] = useUpdateLesionDetailsMutation();
  const { id } = useParams();
  const { state } = useLocation();
  const { lesion: lesionDetails } = (state ?? {}) as EditLesionNavState;
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const navigation = useNavigate();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isSavingLesion, setIsSavingLesion] = useState(false);

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(lesionSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormState>({
    resolver: yupResolver(lesionSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (lesionDetails) {
      // Reset from the handed-over record to avoid stale edits when navigating
      // between rows without a remount.
      reset({
        name: lesionDetails.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesionDetails]);

  /**
   * Submits the updated lesion payload.
   *
   * Args:
   *   values (FormState): Form values for the lesion update.
   *
   * Returns:
   *   Promise<void>
   *
   * Raises:
   *   Error: Propagated when the update mutation fails.
   *
   * Notes:
   * - Uses route `id`; blocks submit when `id` is invalid.
   */
  const onSubmit = async (values: FormState) => {
    try {
      // Block updates if route id is missing/invalid to avoid unintended mutations.
      const parsedId = parseInt(id ?? "", 10);
      if (Number.isNaN(parsedId)) {
        setMessage({
          type: "error",
          text: "Unable to update lesion. Please reload and try again.",
        });
        return;
      }
      setIsSavingLesion(true);
      const { name } = values;
      const payload: EditLesionPayload = {
        id: parsedId,
        name: name,
      };

      await updateLesion(payload).unwrap();
      setIsSavingLesion(false);
      setMessage({
        type: "success",
        text: "Lesion updated successfully.",
      });
      navigationTimeoutRef.current = setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Please enter valid details"),
      });
      setIsSavingLesion(false);
    }
  };
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  if (!lesionDetails) {
    return (
      <Box className={style.container}>
        <Typography className={style.detailHeading} sx={{ marginTop: "10px" }}>
          This lesion is no longer available
        </Typography>
        <Typography sx={{ color: colors.grey, marginTop: "10px" }}>
          Lesion details are carried over from the list screen, so this page
          cannot be opened directly or shared as a link. Open it again from
          Lesions.
        </Typography>
        <Box className={style.buttons}>
          <Button
            sx={{ width: "200px", height: "55px" }}
            variant="contained"
            onClick={() => navigation("/lesions")}
            label={"Back to Lesions"}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Box>
          <Typography
            className={style.detailHeading}
            sx={{ marginTop: "10px" }}
          >
            Lesion Details
          </Typography>
          <Box className={style.flexTop}>
            <Box className={style.row}>
              <Input
                placeholder="e.g Melanoma"
                label="Name"
                required
                register={register}
                name="name"
                errors={errors}
              />
            </Box>
          </Box>
          {message && (
            <Box mt={2}>
              <LabelBox
                boxProps={{ height: 50, display: "flex", alignItems: "center" }}
                data={message}
                onClose={() => setMessage(null)}
              />
            </Box>
          )}
          <Box className={style.buttons}>
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: "transparent" },
              }}
              variant="outlined"
              onClick={() => navigation(-1)}
              label={"Back"}
            />
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: colors.primary },
              }}
              variant="contained"
              type="submit"
              label={"Save"}
              loading={isSavingLesion}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default EditLesion;
